package com.upcominds.expedite.features.challenges

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.ApiException
import com.upcominds.expedite.data.challenges.ChallengesRepository
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.data.user.UserRepository
import com.upcominds.expedite.dto.SocialAccountCreateRequest
import com.upcominds.expedite.dto.SocialAccountDTO
import com.upcominds.expedite.dto.SocialAccountVerificationCompleteRequest
import com.upcominds.expedite.dto.SocialAccountVerificationStartDTO
import com.upcominds.expedite.dto.SocialChallengeSubmissionDTO
import com.upcominds.expedite.dto.SocialChallengeVerifyResultDTO
import com.upcominds.expedite.model.Challenge
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class SocialChallengeUiState(
    val loading: Boolean = false,
    val working: Boolean = false,
    val error: String? = null,
    val challenge: Challenge? = null,
    val socialAccount: SocialAccountDTO? = null,
    val verification: SocialAccountVerificationStartDTO? = null,
    val submissions: List<SocialChallengeSubmissionDTO> = emptyList(),
    val lastResult: SocialChallengeVerifyResultDTO? = null,
    val handleInput: String = "",
    val verificationPostUrl: String = "",
    val challengePostUrl: String = ""
)

class SocialChallengeViewModel(
    private val challengesRepository: ChallengesRepository,
    private val userRepository: UserRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(SocialChallengeUiState())
    val uiState: StateFlow<SocialChallengeUiState> = _uiState.asStateFlow()

    fun load(challengeId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val challenge = challengesRepository.getChallenge(challengeId)
                val platform = challenge.socialConfig?.platform
                val accounts = userRepository.getSocialAccounts()
                val account = platform?.let { p ->
                    accounts.firstOrNull { it.platform.equals(p, ignoreCase = true) }
                }
                val submissions = challengesRepository.getSocialSubmissions(challengeId)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    challenge = challenge,
                    socialAccount = account,
                    handleInput = account?.handle ?: _uiState.value.handleInput,
                    submissions = submissions
                )
            } catch (e: Exception) {
                logMobileError("SocialChallengeViewModel.load", e, "Failed to load social challenge $challengeId")
                _uiState.value = _uiState.value.copy(loading = false, error = toUserMessage(e))
            }
        }
    }

    fun onHandleChange(value: String) {
        _uiState.value = _uiState.value.copy(handleInput = value)
    }

    fun onVerificationPostUrlChange(value: String) {
        _uiState.value = _uiState.value.copy(verificationPostUrl = value)
    }

    fun onChallengePostUrlChange(value: String) {
        _uiState.value = _uiState.value.copy(challengePostUrl = value)
    }

    fun saveHandle() {
        val platform = _uiState.value.challenge?.socialConfig?.platform ?: return
        val handle = _uiState.value.handleInput.trim()
        if (handle.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Add your social handle first.")
            return
        }

        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(working = true, error = null, verification = null)
            try {
                val account = userRepository.upsertSocialAccount(
                    SocialAccountCreateRequest(platform = platform, handle = handle)
                )
                _uiState.value = _uiState.value.copy(working = false, socialAccount = account)
            } catch (e: Exception) {
                logMobileError("SocialChallengeViewModel.saveHandle", e)
                _uiState.value = _uiState.value.copy(working = false, error = toUserMessage(e))
            }
        }
    }

    fun startVerification() {
        val account = _uiState.value.socialAccount ?: return
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(working = true, error = null)
            try {
                val verification = userRepository.startSocialAccountVerification(account.id)
                _uiState.value = _uiState.value.copy(working = false, verification = verification)
            } catch (e: Exception) {
                logMobileError("SocialChallengeViewModel.startVerification", e)
                _uiState.value = _uiState.value.copy(working = false, error = toUserMessage(e))
            }
        }
    }

    fun completeVerification() {
        val account = _uiState.value.socialAccount ?: return
        val postUrl = _uiState.value.verificationPostUrl.trim()
        if (postUrl.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Paste the verification post URL first.")
            return
        }

        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(working = true, error = null)
            try {
                val updated = userRepository.completeSocialAccountVerification(
                    account.id,
                    SocialAccountVerificationCompleteRequest(postUrl = postUrl)
                )
                _uiState.value = _uiState.value.copy(
                    working = false,
                    socialAccount = updated,
                    verification = null,
                    verificationPostUrl = ""
                )
            } catch (e: Exception) {
                logMobileError("SocialChallengeViewModel.completeVerification", e)
                _uiState.value = _uiState.value.copy(working = false, error = toUserMessage(e))
            }
        }
    }

    fun submitChallengePost(challengeId: String) {
        val postUrl = _uiState.value.challengePostUrl
        submitChallengePostUrl(challengeId, postUrl)
    }

    fun submitChallengePostUrl(challengeId: String, url: String) {
        val postUrl = url.trim()
        if (postUrl.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Paste your challenge post URL first.")
            return
        }

        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(working = true, error = null)
            try {
                val result = challengesRepository.verifySocialPost(challengeId, postUrl)
                val submissions = challengesRepository.getSocialSubmissions(challengeId)
                _uiState.value = _uiState.value.copy(
                    working = false,
                    lastResult = result,
                    submissions = submissions,
                    challengePostUrl = ""
                )
            } catch (e: Exception) {
                logMobileError("SocialChallengeViewModel.submitChallengePost", e)
                _uiState.value = _uiState.value.copy(working = false, error = toUserMessage(e))
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    private fun toUserMessage(error: Exception): String {
        val apiError = error as? ApiException ?: return error.message ?: "Something went wrong. Please try again."
        return when (apiError.statusCode) {
            400 -> apiError.message.ifBlank { "The post or account details are invalid." }
            401 -> "Your session has expired. Please sign in again."
            409 -> apiError.message.ifBlank { "This social post could not be accepted yet." }
            in 500..599 -> "Social verification is unavailable right now. Please try again in a bit."
            else -> apiError.message.ifBlank { "Verification failed. Please try again." }
        }
    }
}
